/**
 * What an operator sees when a screen is not theirs to open (ADR-0085).
 *
 * All three authority guards answered a refused URL with `<Navigate to="/" replace />` and no
 * word of explanation — so an operator who followed a colleague's link, or who holds `finance`
 * and not `pricing`, arrived at the landing page with no idea why. The URL was gone from the
 * address bar too, so they could not even quote it when asking.
 *
 * **The redirect survives for one case, and the distinction is the decision.** A Merchant user
 * reaching an Ops URL is not making a mistake that wants explaining: the console is simply not
 * their screen and their Orders are one route away, which is what the guards' own headers argued
 * and which still holds. An **Ops** user who lacks one responsibility is a different thing
 * entirely — they are in the right building and the answer is actionable, so it is said.
 *
 * **It names a responsibility and never a person.** `/ops/people` shows a label and an id and
 * deliberately no email (ADR-0067), and a refusal that said "ask Rana" would put back on this
 * screen the thing that console refuses to show on its own.
 *
 * It is a courtesy, not a control, exactly as before: the API refuses independently with
 * `ops_facet_required`, and nothing here is load-bearing for authority.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * What the screen asked for.
 *
 * Mirrors `nav/screens.ts`'s `Authority` minus the two kinds that cannot be refused — a session
 * is not an authority anyone lacks here, and `staffAndFacet` is enforced as two guards.
 */
export type Needs =
  | { readonly kind: 'staff' }
  | { readonly kind: 'facet'; readonly facet: string }
  | { readonly kind: 'accessAuthority' };

export function AuthorityRefused({ needs }: { readonly needs: Needs }): React.JSX.Element {
  const { t } = useTranslation();

  /*
   * `t(key, fallback)` on the facet name for the reason `refusal.ts:73` uses it: the facet
   * arrives as a string from a route's own prop, so a facet added to the API before its copy
   * lands here renders its own name rather than a missing-key marker.
   */
  const sentence =
    needs.kind === 'staff'
      ? t('refusal.needsStaff')
      : needs.kind === 'accessAuthority'
        ? t('refusal.needsAccessAuthority')
        : t('refusal.needsFacet', { facet: t(`ops.facet.${needs.facet}`, needs.facet) });

  return (
    <section className="card">
      <h2>{t('refusal.noAccess.title')}</h2>
      {/*
        `role="alert"` rather than a plain paragraph: this replaces a page the reader asked for,
        so it is an outcome rather than standing prose — the distinction `Notice` draws, and the
        one that decides whether a screen reader announces it.
      */}
      <p className="notice notice--warn" role="alert">
        {sentence}
      </p>
      <p className="muted">{t('refusal.noAccess.ask')}</p>
      {/* No `card__head` wrapper: this is the foot of the card, and that class name says head. */}
      <p>
        <Link className="btn" to="/">
          {t('nav.home')}
        </Link>
      </p>
    </section>
  );
}
